import { ContractLightModel } from '../contract/types';
import { QuoteModel } from '../quote/types';
import { Action } from '../view/types';
import { CallTabDetailsListItem } from '../telephony/call/types';

export const leadFeatureKey = 'leads';

export type InsuranceLead = {
  id: number;
  leadId: number;
  contractStartDate: Date;
};

export type PushLeadModel = {
  id: string;
  oggo: boolean;
  smartbroker: boolean;
};

export enum InsuranceCategory {
  Undefined = 0,
  Health = 1,
  Car = 2,
  House = 3,
  Bike = 4,
}

export type InsuranceCategoryChoice =
  | 'Undefined'
  | 'Health'
  | 'Car'
  | 'House'
  | 'Bike';

export type LeadModel = {
  id: number;
  uniqueId: string;
  category: InsuranceCategory;
  categoryLoc: string;
  categoryColor: string;
  contactFirstName: string;
  contactLastName: string;
  creationDate: Date;
  contactPhoneNumber: string;
  contactEmail: string;
  contactZipCode: string;
  contactCity: string;
  contactAddress: string;
  fullCity: string;
  salesPersonContactName: string;
  campaignName: string;
  contactName: string;
  contactIsEmployee: boolean;
  contactIsCompany: boolean;
};

export type LeadDetailModel = {
  id: number;
  contactId?: number;
  uniqueId: string;
  parentId?: number;
  parentUniqueId: string;
  category: InsuranceCategory;
  categoryLoc: string;
  categoryColor: string;
  productCategoryId?: number;
  productCategoryName: string;
  productCategoryProductCategoryEnumKey: number;
  name: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  address: string;
  address2: string;
  zipCode: string;
  city: string;
  profession: string;
  professionLoc: string;
  fullCity: string;
  mediumId?: number;
  mediumName: string;
  sourceId?: number;
  sourceName: string;
  salesPersonId: number | null;
  salesPersonContactName: string;
  campaignId: number | null;
  campaignName: string;
  expectedContractStartDate: Date;
  salesTeamId: number | null;
  salesTeamName: string;
  contracts: ContractLightModel[];
  leadStatus: LeadStatus;
  probability: number;
  lastStatusUpdate: Date;
  lostSummary: string;
  creationDate: Date;
  quotes: QuoteModel[];
  contactReferrerId: number;
  contactReferrerName: string;
  duplicateLeads: LeadDuplicateLeadListItemModel[];
  duplicateLeadsCount: number;
  isDuplicate: boolean;
  hasContract: boolean;
  calls: CallTabDetailsListItem[];
};

export type HealthInsuranceLeadModel = {
  routineCareCoverageLevel: number;
  hospitalizationCoverageLevel: number;
  dentalCoverageLevel: number;
  opticalCoverageLevel: number;
  insuredPersons: HealthInsuranceLeadDetailModel[];
};

export type HealthInsuranceLeadDetailModel = {
  id: number;
  insuredTypeLoc: string;
  insuredType: InsuredType;
  socialSecurityKey: RegimeEnum;
  socialSecuritySchemeId: number;
  socialSecurityKeyLoc: string;
  socialSecuritySchemeName: string;
  birthDate: Date;
  age: number;
  lastName: string;
  firstName: string;
};

export type CarInsuranceLeadModel = {
  id: number;
  insuranceLeadLeadId: number;
  driverGender: string;
  driverFirstName: string;
  driverLastName: string;
  driverBirthdate: Date;
  driverZipCode: string;
  driverCity: string;
  driverAddress: string;
  driverMaritalStatus: string;
  driverMaritalStatusLoc: string;
  licenseDate: Date;
  isDriverVehiculeRegistrationHolder: boolean;
  vehiculeRegistrationHolder: string;
  vehiculeRegistrationHolderNameLastName: string;
  vehiculeRegistrationHolderNameFirstName: string;
  vehiculeRegistrationHolderCompanyName: string;
  vehiculeRegistrationHolderBirthdate: Date;
  yearsInsured: string;
  bonusMalus: string;
  bonusMalusLoc: string;
  monthInsuranceInterruption: string;
  monthInsuranceInterruptionLoc: string;
  insuranceTermination: string;
  insuranceTerminationLoc: string;
  licenseAnnulation?: boolean;
  noLicenseConviction?: boolean;
  licenseCancel?: boolean;
  licenseCancelMotive: string;
  licenseCancelMotiveLoc: string;
  licenseCancelDate: Date;
  licenseSuspension?: boolean;
  licenseSuspensionMotive: string;
  licenseSuspensionMotiveLoc: string;
  licenseSuspensionDate: Date;
  longestSuspension: string;
  longestSuspensionLoc: string;
  alcoholDrugControl?: boolean;
  nbCarDisaster: string;
  licensePlateNumber: string;
  brand: string;
  model: string;
  exactModel: string;
  carBody: string;
  carEnergie: string;
  carPower: string;
  vehiculeUsage: string;
  vehiculeUsageLoc: string;
  vehiculeUsageProSupp: string;
  vehiculeUsageProSuppLoc: string;
  parkingType: string;
  parkingTypeLoc: string;
  dateOfCirculation: Date;
  contractStartDate: Date;
  insuranceCoverage: string;
  currentInsuranceCompany: string;
  driverBodyCoverage: boolean;
  replacementVehicleCoverage: boolean;
  driverPropertyCoverage: boolean;
  brandNewCoverage: boolean;
  moneyLossCoverage: boolean;
  carDisasters: CarInsuranceLeadDisasterDetailModel[];
};

export type CarInsuranceLeadDisasterDetailModel = {
  carDisasterDate: Date;
  carDisasterReason: string;
  carDisasterResponsiblePerson: string;
};

export type HouseInsuranceLeadModel = {
  address: string;
  zipCode: string;
  city: string;
  maritalStatus: string;
  profession: string;
  housingType: string;
  occupancyRole: string;
  occupancyRoleLoc: string;
  nbOfRooms: number;
  area: number;
  constructionType: string;
  roofType: string;
  amountToInsure: string;
  flatLevel: string;
  residencyType: string;
  fireCoverage: boolean;
  waterCoverage: boolean;
  theftCoverage: boolean;
  glassBreakCoverage: boolean;
  publicLiabilityCoverage: boolean;
  feeCancellationCoverage: boolean;
  electricalDamageCoverage: boolean;
  poolCoverage: boolean;
  dogCoverage: boolean;
  currentInsuranceCompany: string;
};

export enum InsuredType {
  Holder = 'holder',
  Partner = 'partner',
  Child = 'child',
}

export enum RegimeEnum {
  SECURITE_SOCIALE = 1,
  TNS = 2,
  EXPLOITANT_AGRICOLE = 3,
  SALARIE_AGRICOLE = 4,
  ALSACE_MOSELLE = 5,
  FONCTIONNAIRE_TERRITORIAL = 6,
}

export type BaseLeadFormModel = {
  leadFormModel: LeadFormModel;
  contactFormModel: ContactFormModel;
};

export type HealthLeadFormModel = {
  baseLeadFormModel: BaseLeadFormModel;
  healthInsuranceLeadFormModel: HealthInsuranceLeadFormModel;
  insureds: HealthInsuranceLeadDetailEditModel[];
};

export type CarLeadFormModel = {
  baseLeadFormModel: BaseLeadFormModel;
  carInsuranceLeadModel: CarInsuranceLeadModel;
  disasters: CarInsuranceLeadDisasterDetailFormModel[];
};

export type HouseLeadFormModel = {
  baseLeadFormModel: BaseLeadFormModel;
  houseInsuranceLeadFormModel: HouseInsuranceLeadFormModel;
};

export type LeadFormModel = {
  id?: number;
  category: InsuranceCategory;
  categoryLoc: string;
  categoryColor?: string;
  productCategoryId?: number;
  title: string;
  firstName: string;
  lastName: string;
  profession: string;
  phoneNumber: string;
  email: string;
  zipCode: string;
  city: string;
  address: string;
  address2: string;
  salesTeamId: number;
  salesPersonId: number;
  campaignId: number;
  mediumId?: number;
  sourceId?: number;
  creationDate: Date;
  expectedContractStartDate: Date;
};

export type ContactFormModel = {
  id?: number;
  firstName: string;
  lastName: string;
  address: string;
  address2: string;
  zipCode: string;
  city: string;
  phoneNumber: string;
  email: string;
  profession: string;
  referrerId: number;
};

export type InsuranceLeadFormModel = {
  contractStartDate: Date;
};

export type HealthInsuranceLeadFormModel = {
  routineCareCoverageLevel: number;
  hospitalizationCoverageLevel: number;
  dentalCoverageLevel: number;
  opticalCoverageLevel: number;
  county: string;
};

export type HealthInsuranceLeadDetailEditModel = {
  insuredType: InsuredType;
  socialSecurityKey?: RegimeEnum;
  socialSecuritySchemeId: number;
  firstName: string;
  lastName: string;
  birthDate: Date;
};

// export type CarInsuranceLeadFormModel = {
//   driverFirstName: string;
//   driverLastName: string;
//   driverBirthdate: Date;
//   driverZipCode: string;
//   driverCity: string;
//   driverAddress: string;
//   licenseDate: Date;
//   isDriverVehiculeRegistrationHolder: boolean;
//   vehiculeRegistrationHolder: string;
//   vehiculeRegistrationHolderNameLastName: string;
//   vehiculeRegistrationHolderNameFirstName: string;
//   vehiculeRegistrationHolderCompanyName: string;
//   vehiculeRegistrationHolderBirthdate: Date;
//   yearsInsured: string;
//   bonusMalus: string;
//   monthInsuranceInterruption: string;
//   insuranceTermination: string;
//   licenseAnnulation: string;
//   noLicenseConviction: string;
//   licenseCancel: string;
//   licenseCancelMotive: string;
//   licenseCancelDate: Date;
//   licenseSuspension: string;
//   licenseSuspensionMotive: string;
//   licenseSuspensionDate: Date;
//   longestSuspension: string;
//   alcoholDrugControl: string;
//   nbCarDisaster: string;
//   licensePlateNumber: string;
//   brand: string;
//   model: string;
//   exactModel: string;
//   carBody: string;
//   carEnergie: string;
//   carPower: string;
//   vehiculeUsage: string;
//   vehiculeUsageProSupp: string;
//   parkingType: string;
//   dateOfCirculation: Date;
//   contractStartDate: Date;
//   insuranceCoverage: string;
//   currentInsuranceCompany: string;
//   driverBodyCoverage: boolean;
//   replacementVehicleCoverage: boolean;
//   driverPropertyCoverage: boolean;
//   brandNewCoverage: boolean;
//   moneyLossCoverage: boolean;
// };

export type CarInsuranceLeadDisasterDetailFormModel = {
  carDisasterDate: Date;
  carDisasterReason: string;
  carDisasterResponsiblePerson: string;
};

export type HouseInsuranceLeadFormModel = {
  address: string;
  zipCode: string;
  city: string;
  maritalStatus: string;
  profession: string;
  housingType: string;
  occupancyRole: string;
  nbOfRooms: number;
  area: number;
  constructionType: string;
  roofType: string;
  amountToInsure: string;
  flatLevel: string;
  residencyType: string;
  fireCoverage: boolean;
  waterCoverage: boolean;
  theftCoverage: boolean;
  glassBreakCoverage: boolean;
  publicLiabilityCoverage: boolean;
  feeCancellationCoverage: boolean;
  electricalDamageCoverage: boolean;
  poolCoverage: boolean;
  dogCoverage: boolean;
  currentInsuranceCompany: string;
};

export type LeadStatusFlow = {
  label: string;
  status: LeadStatus;
  statusId: number;
  statusColor: string;
};

export type LeadStatus = 'New' | 'Qualified' | 'Proposition' | 'Won' | 'Lost';
export type LeadDuplicateType =
  | 'Principal'
  | 'SameRequest'
  | 'CameBackLater'
  | 'Duplicate';

export type LeadLostModel = {
  leadId: number;
  leadLostReason: number;
  leadLostSummary: string;
};

export type InputReferenceData = {
  endpoint: string;
  field: string;
  filterParentName: string;
  filterParentValue: string;
  filterGrandParentName: string;
  filterGrandParentValue: string;
  orderBy: string;
};

export type LeadDuplicateLeadListItemModel = {
  id: number;
  uniqueId: string;
  parentId?: number | null;
  contactId?: number | null;
  creationDate: Date;
  duplicateType?: LeadDuplicateType | null;
  displayedDuplicateType: LeadDuplicateType;
  duplicateTypeLoc: string;
  duplicateTypeColor: string;
  productCategoryName: string;
  sourceName: string;
  mediumName: string;
  campaignName: string;
  leadStatus: LeadStatus;
  leadStatusLoc: string;
  statusColor: string;
  probability: number;
};

export type LeadMarkAsDuplicateParams = {
  leadUniqueId: string;
  parentLeadUniqueId: string;
};

export type LeadPrincipalCandidateListItemModel = {
  id: number;
  uniqueId: string;
  contactId?: number | null;
  lastName: string;
  firstName: string;
  email: string;
  phoneNumber: string;
  creationDate: Date;
  productCategoryName: string;
  sourceName: string;
  mediumName: string;
  campaignName: string;
  duplicatesCount: number;
  probability: number;
  leadStatus: LeadStatus;
  leadStatusLoc: string;
  statusColor: string;
  actions: Action[];
};
